/**
 * What happens to a long-text answer — Story 20.7, DECISION-141.
 *
 * PRD §3.6: "Adding a long-text field does *not* automatically hand it to the
 * AI. The operator chooses." This is where they choose. A new question is still
 * created in `capture_only` (`newBlock` in `editor/blocks.ts`); the control only
 * ever changes a mode somebody pressed.
 *
 * TWO MODES AND NO THIRD. PRD §3.6 also names `rules`, keyword and length
 * matching, and DECISION-141 keeps it out: offering it means a keyword and
 * length editor nothing draws, and a mode with nothing behind it is a value that
 * does nothing. A question already in `rules` — which only the API can write —
 * shows neither option pressed, and either one moves it.
 *
 * LONG TEXT ONLY, though `short_text` carries the same field. The decision and
 * the drawing both name a long-text question, and Story 20.2's source select
 * offers long-text questions alone, so a short-text question handed to
 * extraction here would be read by no field the dashboard can add.
 *
 * THE REFUSAL IS THE CONTRACT'S, SURFACED EARLY. `crossReference` refuses an
 * extraction field whose question is not in `ai_extraction` mode, so moving a
 * question out of it while a field reads it writes a funnel the next autosave
 * refuses. `modeRefusal` asks that question before the edit, naming the fields,
 * which is the treatment Story 20.3 gave a field's removal (`removalRefusal` in
 * `editor/extraction.ts`).
 */
import type { Block, FreeTextMode, Funnel } from '@leadgible/schema'
import { COMPOSED, DRAWN } from './answer-mode-copy'
import { joinNames } from './extraction'

/** What the control offers, in the drawn order: Keep it with the lead, then Pull facts out of it. */
export const OFFERED_MODES = ['capture_only', 'ai_extraction'] as const satisfies readonly FreeTextMode[]
export type OfferedMode = (typeof OFFERED_MODES)[number]

/**
 * Each offered mode's drawn label. The control renders `OFFERED_MODES` through
 * this, so a mode added there reaches the screen only with a label beside it —
 * and the type refuses the addition until it has one.
 */
export const MODE_LABEL: Record<OfferedMode, string> = {
  capture_only: DRAWN.keep,
  ai_extraction: DRAWN.pull,
}

export type LongText = Extract<Block, { type: 'long_text' }>

/** The questions the control appears on. */
export const hasAnswerMode = (block: Block): block is LongText => block.type === 'long_text'

/** The keys of the extraction fields that read this question, in the rubric's order. */
export function fieldsReading(funnel: Funnel, blockId: string): string[] {
  return funnel.rubric.extraction.filter((field) => field.block_id === blockId).map((field) => field.field_key)
}

/** "Read by 3 fields: …", or null when no field reads the question. */
export function readByLine(keys: readonly string[]): string | null {
  if (keys.length === 0) return null
  if (keys.length === 1) return COMPOSED.readByOne(keys[0]!)
  return DRAWN.readBy(keys.length, joinNames([...keys]))
}

/**
 * Why this question cannot move to `next`, or null when it can.
 *
 * Moving INTO `ai_extraction` is never refused: nothing can read a question
 * that is not in that mode yet. Moving out of it is refused while any field
 * reads the question, however many — the refusal names all of them.
 */
export function modeRefusal(funnel: Funnel, blockId: string, next: FreeTextMode): string | null {
  if (next === 'ai_extraction') return null
  const keys = fieldsReading(funnel, blockId)
  if (keys.length === 0) return null
  if (keys.length === 1) return COMPOSED.refusedOne(keys[0]!)
  return DRAWN.refused(joinNames(keys))
}

/**
 * Sets a long-text question's mode, and nothing else.
 *
 * A refused move changes nothing, so it produces no patch: `edit()` records no
 * undo step and the draft is not marked for saving — refused before anything is
 * saved, as the drawing says. Choosing the mode a question already has is the
 * same no-op.
 */
export function setAnswerMode(draft: Funnel, blockId: string, next: OfferedMode): void {
  if (modeRefusal(draft, blockId, next) !== null) return
  const target = draft.blocks.find((block) => block.id === blockId)
  if (target === undefined || !hasAnswerMode(target)) return
  if (target.free_text_mode === next) return
  target.free_text_mode = next
}
